import SeoHead from "@/components/site/SeoHead";
import Header from "@/components/site/Header";
import Hero from "@/components/site/Hero";
import HowItWorks from "@/components/site/HowItWorks";
import Benefits from "@/components/site/Benefits";
import IdealFor from "@/components/site/IdealFor";
import Reviews from "@/components/site/Reviews";
import TrustStrip from "@/components/site/TrustStrip";
import FAQSection from "@/components/site/FAQSection";
import ContactSection from "@/components/site/ContactSection";
import Footer from "@/components/site/Footer";
import StickyMobileBar from "@/components/site/StickyMobileBar";
import WhatsAppButton from "@/components/site/WhatsAppButton";
import { COMPANY, FAQS, HERO_VIDEO, HERO_POSTER, SERVICE_CITIES } from "@/data/content";

const TITLE = "Hausnotruf für Senioren – Sicherheit auf Knopfdruck | Schössow";
const DESC = "Hausnotruf für ältere & alleinlebende Menschen: Auf Knopfdruck 24h-Hilfe zu Hause. Wasserdichter SOS-Sender, Freisprech-Basisstation, Notrufzentrale rund um die Uhr. Jetzt kostenlos beraten lassen – Dortmund, Ruhrgebiet & deutschlandweit.";

export default function LandingPage() {
  // Invisible background SEO signal only (no user-visible city section):
  // broad areaServed list so the single page can be relevant for many
  // "Hausnotruf <Stadt>" queries without any city subpages.
  const areaServed = [
    { "@type": "Country", name: "Deutschland" },
    ...SERVICE_CITIES.map((c) => ({ "@type": "City", name: c })),
  ];

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": COMPANY.domain + "/#localbusiness",
    name: COMPANY.name,
    image: HERO_POSTER,
    url: COMPANY.domain,
    telephone: COMPANY.phoneHref,
    email: COMPANY.email,
    priceRange: "€€",
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY.street,
      postalCode: COMPANY.zip,
      addressLocality: COMPANY.city,
      addressCountry: "DE",
    },
    areaServed,
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
    description: DESC,
  };
  // NOTE: We intentionally do NOT add aggregateRating to LocalBusiness.
  // Self-serving ratings on your own business are not eligible for Google
  // review rich results and trigger Search Console structured-data errors.
  // The visible Google-rating box on the page (linking to the real Google
  // profile) remains for visitors and is unaffected.

  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Hausnotruf / Senioren-Notrufsystem",
    provider: { "@type": "Organization", name: COMPANY.name, telephone: COMPANY.phoneHref },
    areaServed,
    name: "Hausnotruf – Senioren SOS System für Zuhause",
    description: "Notrufsystem mit Basisstation und wasserdichtem SOS-Sender (Armband/Kette). Auf Knopfdruck wird eine 24-Stunden-Notrufzentrale erreicht.",
  };

  const faqPage = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const videoObject = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "Hausnotruf – So funktioniert die Hilfe auf Knopfdruck",
    description: "Ein Hausnotruf sorgt dafür, dass Senioren im Notfall per Knopfdruck schnell Hilfe erhalten.",
    thumbnailUrl: [HERO_POSTER],
    uploadDate: "2025-01-01",
    contentUrl: HERO_VIDEO,
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Start", item: COMPANY.domain },
    ],
  };

  return (
    <>
      <SeoHead
        title={TITLE}
        description={DESC}
        canonical={COMPANY.domain + "/"}
        jsonLd={[localBusiness, service, faqPage, videoObject, breadcrumb]}
      />
      <Header />
      <main>
        <Hero />
        <TrustStrip />
        <HowItWorks />
        <Benefits />
        <IdealFor />
        <Reviews />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
      <StickyMobileBar />
      <WhatsAppButton />
      <div className="h-20 lg:hidden" aria-hidden="true" />
    </>
  );
}
